import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { subscriptionsAPI, productsAPI, paymentsAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SizeCombobox } from '../../components/ui/size-combobox';
import {
  ArrowLeft, RefreshCw, Calendar, Package, XCircle, CheckCircle,
  Clock, Pause, Play, SkipForward, Edit, MapPin, Loader2, Plus, Trash2, CreditCard, AlertTriangle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';

const STATUS_STYLES = {
  active: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Active' },
  pending_cancel: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Cancelling' },
  cancelled: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Cancelled' },
  paused: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-300', label: 'Paused' },
  payment_failed: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300', label: 'Payment Failed' },
};

const MERV_RATINGS = [8, 11, 13];

// Payment method management removed — QBO Payments handles payment at checkout
const UpdateCardForm = ({ subscriptionId, onSuccess, onCancel }) => {
  return (
    <div className="space-y-4 text-center py-4">
      <p className="text-sm text-slate-600">Payments are now processed through QuickBooks at checkout time.</p>
      <p className="text-xs text-slate-400">No saved cards required — you&apos;ll enter payment details when placing each order.</p>
      <Button type="button" variant="outline" onClick={onCancel}>Close</Button>
    </div>
  );
};

const Subscriptions = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);

  // Dialog states
  const [cancelId, setCancelId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [skipId, setSkipId] = useState(null);
  const [editItemsSub, setEditItemsSub] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editAddrSub, setEditAddrSub] = useState(null);
  const [editAddr, setEditAddr] = useState('');
  const [updateCardSub, setUpdateCardSub] = useState(null);

  const navigate = useNavigate();
  const { toast } = useToast();

  const loadSubs = async () => {
    try {
      const data = await subscriptionsAPI.getAll();
      setSubs(data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load subscriptions', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubs();
    productsAPI.getAll().then(setProducts).catch(() => {});
  }, []);

  const handleAction = async (action, subId, payload) => {
    setProcessing(true);
    try {
      let result;
      switch (action) {
        case 'cancel': result = await subscriptionsAPI.cancel(subId); break;
        case 'reactivate': result = await subscriptionsAPI.reactivate(subId); break;
        case 'pause': result = await subscriptionsAPI.pause(subId); break;
        case 'resume': result = await subscriptionsAPI.resume(subId); break;
        case 'skip': result = await subscriptionsAPI.skip(subId); break;
        case 'updateItems': result = await subscriptionsAPI.updateItems(subId, payload); break;
        case 'updateAddress': result = await subscriptionsAPI.updateAddress(subId, payload); break;
        default: return;
      }
      toast({ title: 'Success', description: result.message || 'Updated' });
      setCancelId(null);
      setSkipId(null);
      setEditItemsSub(null);
      setEditAddrSub(null);
      loadSubs();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const openEditItems = (sub) => {
    setEditItemsSub(sub);
    setEditItems(sub.items.map(i => ({ ...i })));
  };

  const updateEditItem = (idx, field, val) => {
    setEditItems(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: field === 'quantity' ? parseInt(val) || 1 : field === 'price' ? parseFloat(val) || 0 : val };
      return updated;
    });
  };

  const addEditItem = () => setEditItems(prev => [...prev, { size: '', merv: 8, quantity: 1, price: 0 }]);
  const removeEditItem = (idx) => { if (editItems.length > 1) setEditItems(prev => prev.filter((_, i) => i !== idx)); };

  const fetchItemPrice = async (idx, size, merv) => {
    if (!size || !merv) return;
    try {
      const pricing = await productsAPI.getPricing(size, parseInt(merv));
      setEditItems(prev => {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], price: pricing.price };
        return updated;
      });
    } catch (e) {
      console.error('Subscription update failed:', e);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto" data-testid="subscriptions-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="subscriptions-title">My Subscriptions</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your auto-delivery subscriptions</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/customer/dashboard')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>

        {subs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border" data-testid="no-subscriptions">
            <RefreshCw className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Active Subscriptions</h3>
            <p className="text-sm text-slate-500 mb-4">Set up an auto-delivery subscription for hassle-free filter replacement.</p>
            <Button onClick={() => navigate('/customer/new-order')} className="bg-gradient-to-r from-sky-500 to-teal-500"
              data-testid="start-subscription-btn">
              Start a Subscription
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {subs.map(sub => {
              const style = STATUS_STYLES[sub.status] || STATUS_STYLES.active;
              const totalPerOrder = sub.items.reduce((s, i) => s + (i.price || 0) * (i.quantity || 0), 0);
              const discountAmt = totalPerOrder * ((sub.discount_percent || 0) / 100);
              const isEditable = !['cancelled'].includes(sub.status);
              const hasPaymentMethod = false; // QBO doesn't store card on file

              return (
                <div key={sub.id} className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid={`subscription-${sub.id}`}>
                  {/* Header */}
                  <div className="px-5 py-3 bg-slate-50 border-b flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <RefreshCw className="h-5 w-5 text-teal-600" />
                      <span className="font-bold text-sm text-slate-700">{sub.id}</span>
                      <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${style.bg} ${style.text} ${style.border}`}>
                        {style.label}
                      </span>
                      {hasPaymentMethod && (
                        <Badge variant="outline" className="text-xs gap-1 text-sky-600 border-sky-200">
                          <CreditCard className="h-3 w-3" /> Auto-Pay
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Calendar className="h-3.5 w-3.5" />
                      Created {formatDate(sub.created_at)}
                    </div>
                  </div>

                  {/* Payment failed warning */}
                  {sub.status === 'payment_failed' && (
                    <div className="px-5 py-3 bg-red-50 border-b border-red-200 flex items-center gap-2 text-sm text-red-700" data-testid={`payment-failed-${sub.id}`}>
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      <span>Last auto-payment failed. Please update your payment method or reactivate to retry.</span>
                    </div>
                  )}

                  {/* Body */}
                  <div className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Schedule</p>
                        <p className="text-sm font-semibold text-slate-700">Every {sub.recurrence_months} months</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Next Delivery</p>
                        <p className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5 text-sky-500" />
                          {sub.status === 'cancelled' ? 'N/A' : sub.status === 'paused' ? 'Paused' : formatDate(sub.next_delivery)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Est. Per Order</p>
                        <p className="text-sm font-bold text-slate-700">${totalPerOrder.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Savings Per Order</p>
                        <p className="text-sm font-bold text-teal-600">${discountAmt.toFixed(2)} ({sub.discount_percent}% off)</p>
                      </div>
                    </div>

                    {/* Shipping Address */}
                    <div className="bg-slate-50 rounded-lg p-3 mb-3 flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-bold text-slate-500 uppercase mb-0.5">Ship To</p>
                          <p className="text-sm text-slate-700">{sub.shipping_address || 'No address set'}</p>
                        </div>
                      </div>
                      {isEditable && (
                        <Button variant="ghost" size="sm" className="text-xs text-sky-600 hover:text-sky-800 h-7 px-2"
                          onClick={() => { setEditAddrSub(sub); setEditAddr(sub.shipping_address || ''); }}
                          data-testid={`edit-addr-${sub.id}`}>
                          <Edit className="h-3 w-3 mr-1" /> Edit
                        </Button>
                      )}
                    </div>

                    {/* Items */}
                    <div className="bg-slate-50 rounded-lg p-3 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold text-slate-500 uppercase">Items</p>
                        {isEditable && (
                          <Button variant="ghost" size="sm" className="text-xs text-sky-600 hover:text-sky-800 h-7 px-2"
                            onClick={() => openEditItems(sub)}
                            data-testid={`edit-items-${sub.id}`}>
                            <Edit className="h-3 w-3 mr-1" /> Edit Items
                          </Button>
                        )}
                      </div>
                      <div className="space-y-1">
                        {sub.items.map((item, i) => (
                          <div key={`${item.size}-${item.merv}-${i}`} className="flex items-center justify-between text-sm">
                            <span className="text-slate-600 flex items-center gap-2">
                              <Package className="h-3.5 w-3.5 text-slate-400" />
                              {item.size} — MERV {item.merv} &times; {item.quantity}
                            </span>
                            <span className="font-semibold text-slate-700">${((item.price || 0) * (item.quantity || 0)).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {sub.status === 'active' && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => handleAction('pause', sub.id)}
                            className="text-slate-600 border-slate-300 hover:bg-slate-100"
                            data-testid={`pause-sub-${sub.id}`}>
                            <Pause className="h-4 w-4 mr-1" /> Pause
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setSkipId(sub)}
                            className="text-amber-600 border-amber-200 hover:bg-amber-50"
                            data-testid={`skip-sub-${sub.id}`}>
                            <SkipForward className="h-4 w-4 mr-1" /> Skip Next
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setCancelId(sub.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`cancel-sub-${sub.id}`}>
                            <XCircle className="h-4 w-4 mr-1" /> Cancel
                          </Button>
                        </>
                      )}
                      {sub.status === 'paused' && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => handleAction('resume', sub.id)}
                            className="text-green-600 border-green-200 hover:bg-green-50"
                            data-testid={`resume-sub-${sub.id}`}>
                            <Play className="h-4 w-4 mr-1" /> Resume
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setCancelId(sub.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`cancel-sub-paused-${sub.id}`}>
                            <XCircle className="h-4 w-4 mr-1" /> Cancel
                          </Button>
                        </>
                      )}
                      {sub.status === 'pending_cancel' && (
                        <>
                          <p className="text-xs text-amber-600 mr-auto flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            Cancels at end of cycle ({formatDate(sub.cancel_effective)})
                          </p>
                          <Button variant="outline" size="sm" onClick={() => handleAction('reactivate', sub.id)}
                            className="text-green-600 border-green-200 hover:bg-green-50"
                            data-testid={`reactivate-sub-${sub.id}`}>
                            <CheckCircle className="h-4 w-4 mr-1" /> Reactivate
                          </Button>
                        </>
                      )}
                      {sub.status === 'payment_failed' && (
                        <Button variant="outline" size="sm" onClick={() => handleAction('reactivate', sub.id)}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                          data-testid={`retry-sub-${sub.id}`}>
                          <RefreshCw className="h-4 w-4 mr-1" /> Retry / Reactivate
                        </Button>
                      )}
                      {isEditable && (
                        <Button variant="outline" size="sm" onClick={() => setUpdateCardSub(sub)}
                          className="text-sky-600 border-sky-200 hover:bg-sky-50 ml-auto"
                          data-testid={`update-card-${sub.id}`}>
                          <CreditCard className="h-4 w-4 mr-1" /> {hasPaymentMethod ? 'Update Card' : 'Add Card'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={!!cancelId} onOpenChange={() => setCancelId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Your subscription will remain active until the end of your current delivery cycle. You can reactivate anytime before then.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelId(null)}>Keep Subscription</Button>
            <Button onClick={() => handleAction('cancel', cancelId)} disabled={processing}
              className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-cancel-btn">
              {processing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Cancelling...</> : 'Confirm Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Skip Next Delivery Dialog */}
      <Dialog open={!!skipId} onOpenChange={() => setSkipId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><SkipForward className="h-5 w-5 text-amber-600" /> Skip Next Delivery</DialogTitle>
            <DialogDescription>
              Your next delivery on <strong>{skipId ? formatDate(skipId.next_delivery) : ''}</strong> will be skipped.
              The following delivery will be pushed forward by one cycle ({skipId?.recurrence_months} months).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSkipId(null)}>Keep Delivery</Button>
            <Button onClick={() => handleAction('skip', skipId?.id)} disabled={processing}
              className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-skip-btn">
              {processing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Skipping...</> : 'Skip This Delivery'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Items Dialog */}
      <Dialog open={!!editItemsSub} onOpenChange={() => setEditItemsSub(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Subscription Items</DialogTitle>
            <DialogDescription>Update the filters in your subscription. Price changes take effect on the next delivery.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto" data-testid="edit-items-dialog">
            {editItems.map((item, idx) => (
              <div key={`edit-${item.size || ''}-${item.merv || ''}-${idx}`} className="grid grid-cols-12 gap-2 items-end" data-testid={`edit-row-${idx}`}>
                <div className="col-span-3">
                  {idx === 0 && <Label className="text-xs mb-1">MERV</Label>}
                  <Select value={String(item.merv)} onValueChange={v => {
                    updateEditItem(idx, 'merv', parseInt(v));
                    fetchItemPrice(idx, item.size, v);
                  }}>
                    <SelectTrigger className="h-9" data-testid={`edit-merv-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{MERV_RATINGS.map(m => <SelectItem key={m} value={String(m)}>MERV {m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-4">
                  {idx === 0 && <Label className="text-xs mb-1">Size</Label>}
                  <SizeCombobox
                    value={item.size}
                    onValueChange={v => {
                      updateEditItem(idx, 'size', v);
                      fetchItemPrice(idx, v, item.merv);
                    }}
                    flatProducts={products}
                    placeholder="Search size..."
                    className="h-9"
                    data-testid={`edit-size-${idx}`}
                  />
                </div>
                <div className="col-span-2">
                  {idx === 0 && <Label className="text-xs mb-1">Qty</Label>}
                  <Input type="number" min={1} className="h-9 text-center" value={item.quantity}
                    onChange={e => updateEditItem(idx, 'quantity', e.target.value)} data-testid={`edit-qty-${idx}`} />
                </div>
                <div className="col-span-2 text-right">
                  {idx === 0 && <Label className="text-xs mb-1">Price</Label>}
                  <p className="h-9 flex items-center justify-end text-sm font-semibold text-slate-700">
                    {item.price > 0 ? `$${item.price.toFixed(2)}` : '-'}
                  </p>
                </div>
                <div className="col-span-1 flex justify-center">
                  {editItems.length > 1 && (
                    <button onClick={() => removeEditItem(idx)} className="text-red-400 hover:text-red-600 h-9 flex items-center">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" className="w-full border-dashed" onClick={addEditItem} data-testid="add-edit-item-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItemsSub(null)}>Cancel</Button>
            <Button onClick={() => handleAction('updateItems', editItemsSub?.id, editItems)} disabled={processing}
              data-testid="save-items-btn">
              {processing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...</> : 'Save Items'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog open={!!editAddrSub} onOpenChange={() => setEditAddrSub(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-sky-600" /> Update Shipping Address</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="edit-addr-dialog">
            <div>
              <Label>Shipping Address</Label>
              <Input className="mt-1" value={editAddr} onChange={e => setEditAddr(e.target.value)}
                placeholder="Enter full shipping address" data-testid="edit-addr-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAddrSub(null)}>Cancel</Button>
            <Button onClick={() => handleAction('updateAddress', editAddrSub?.id, editAddr)} disabled={processing || !editAddr.trim()}
              data-testid="save-addr-btn">
              {processing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...</> : 'Save Address'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Card Dialog */}
      <Dialog open={!!updateCardSub} onOpenChange={() => setUpdateCardSub(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-sky-600" /> Update Payment Method</DialogTitle>
            <DialogDescription>
              Enter your card details below. This card will be used for future auto-payments on subscription <strong>{updateCardSub?.id}</strong>.
            </DialogDescription>
          </DialogHeader>
          <UpdateCardForm
            subscriptionId={updateCardSub?.id}
            onSuccess={() => { setUpdateCardSub(null); loadSubs(); }}
            onCancel={() => setUpdateCardSub(null)}
          />
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Subscriptions;
